<template>
  <div v-if="visible" class="forum_guide" role="dialog" aria-modal="true" aria-label="论坛新手指引">
    <div class="forum_guide__hole" :style="holeStyle" />
    <section ref="tooltip" :class="['forum_guide__tooltip', `forum_guide__tooltip--${tooltipPlacement}`]" :style="tooltipStyle">
      <span class="forum_guide__progress">{{ stepIndex + 1 }}/{{ total }}</span>
      <h3 class="forum_guide__title">{{ step.title }}</h3>
      <p class="forum_guide__content">{{ step.content }}</p>
      <div class="forum_guide__footer">
        <button type="button" class="forum_guide__button" :disabled="actionLoading" @click="onAction">
          {{ actionLoading ? '提交中...' : isLastStep ? '我知道了' : '下一步' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ForumGuide',
  props: {
    visible: Boolean,
    step: { type: Object, required: true },
    stepIndex: { type: Number, required: true },
    total: { type: Number, required: true },
    target: { type: [Object, HTMLElement], default: null },
    targetPadding: { type: Number, default: 4 },
    targetRadius: { type: Number, default: 10 },
    actionLoading: Boolean,
  },
  data() {
    return {
      holeRect: { left: 0, top: 0, width: 0, height: 0 },
      tooltipPosition: { left: 20, top: 20 },
      tooltipArrowCenter: 28,
      tooltipPlacement: 'below',
      positionFrame: 0,
    };
  },
  computed: {
    isLastStep() { return this.stepIndex === this.total - 1; },
    holeStyle() {
      return {
        left: `${this.holeRect.left}px`, top: `${this.holeRect.top}px`,
        width: `${this.holeRect.width}px`, height: `${this.holeRect.height}px`,
        borderRadius: `${this.targetRadius}px`,
      };
    },
    tooltipStyle() {
      return {
        left: `${this.tooltipPosition.left}px`, top: `${this.tooltipPosition.top}px`,
        '--forum-guide-arrow-center': `${this.tooltipArrowCenter}px`,
      };
    },
  },
  watch: {
    visible() { this.queueUpdate(); },
    stepIndex() { this.queueUpdate(); },
    target() { this.queueUpdate(); },
  },
  mounted() {
    window.addEventListener('resize', this.schedulePosition);
    window.addEventListener('scroll', this.schedulePosition, true);
    this.queueUpdate();
  },
  beforeDestroy() {
    if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
    window.removeEventListener('resize', this.schedulePosition);
    window.removeEventListener('scroll', this.schedulePosition, true);
  },
  methods: {
    queueUpdate() { this.$nextTick(this.updatePosition); },
    schedulePosition() {
      if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
      this.positionFrame = requestAnimationFrame(() => {
        this.positionFrame = 0;
        this.updatePosition();
      });
    },
    updatePosition() {
      if (!(this.visible && this.target)) return;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      const rect = this.target.getBoundingClientRect();
      const edge = 8;
      const rightEdge = viewportWidth - edge;
      const left = Math.min(rightEdge, Math.max(edge, rect.left - this.targetPadding));
      const top = Math.max(edge, rect.top - this.targetPadding);
      const right = Math.max(edge, Math.min(rightEdge, rect.right + this.targetPadding));
      const bottom = Math.min(viewportHeight - edge, rect.bottom + this.targetPadding);
      this.holeRect = { left: Math.round(left), top: Math.round(top), width: Math.max(0, Math.round(right - left)), height: Math.max(0, Math.round(bottom - top)) };
      this.$nextTick(() => {
        const tooltip = this.$refs.tooltip;
        const tooltipHeight = tooltip ? tooltip.offsetHeight : 0;
        const tooltipWidth = tooltip ? tooltip.offsetWidth : 0;
        const gap = 30;
        const pagePadding = 20;
        const below = viewportHeight - bottom >= tooltipHeight + gap + pagePadding;
        const targetCenter = (left + right) / 2;
        const maxLeft = Math.max(pagePadding, viewportWidth - pagePadding - tooltipWidth);
        const nextLeft = Math.max(pagePadding, Math.min(targetCenter - tooltipWidth / 2, maxLeft));
        this.tooltipPlacement = below ? 'below' : 'above';
        this.tooltipPosition = { left: Math.round(nextLeft), top: Math.round(below ? bottom + gap : Math.max(pagePadding, top - tooltipHeight - gap)) };
        this.tooltipArrowCenter = Math.round(Math.min(Math.max(18, targetCenter - nextLeft), tooltipWidth - 18));
      });
    },
    onAction() {
      if (this.actionLoading) return;
      this.$emit(this.isLastStep ? 'finish' : 'next');
    },
  },
};
</script>

<style scoped>
.forum_guide { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 1000; overflow: hidden; }
.forum_guide__hole { position: fixed; z-index: 1; pointer-events: none; box-shadow: 0 0 0 2px #ff942f, 0 0 12px 4px rgba(255,132,31,.48), 0 0 0 9999px rgba(0,0,0,.58); transition: left .16s ease, top .16s ease, width .16s ease, height .16s ease; }
.forum_guide__tooltip { position: fixed; z-index: 2; width: calc(100% - 40px); max-width: 280px; padding: 28px 20px 16px; color: #222; background: #fff; border: 2px solid #ff9a3d; border-radius: 12px; box-sizing: border-box; box-shadow: 0 0 10px rgba(255,133,31,.42), 0 8px 24px rgba(0,0,0,.14); }
.forum_guide__tooltip::before { position: absolute; left: var(--forum-guide-arrow-center); width: 3px; height: 24px; background: #ff8a1f; border-radius: 2px; transform: translateX(-50%); content: ''; }
.forum_guide__tooltip::after { position: absolute; left: var(--forum-guide-arrow-center); width: 0; height: 0; border-right: 7px solid transparent; border-left: 7px solid transparent; transform: translateX(-50%); content: ''; }
.forum_guide__tooltip--below::before { top: -24px; }
.forum_guide__tooltip--below::after { top: -32px; border-bottom: 10px solid #ff8a1f; }
.forum_guide__tooltip--above::before { bottom: -24px; }
.forum_guide__tooltip--above::after { bottom: -32px; border-top: 10px solid #ff8a1f; }
.forum_guide__progress { position: absolute; top: 8px; left: 10px; min-width: 42px; height: 22px; padding: 0 7px; color: #fff; font-size: 14px; font-weight: 700; line-height: 22px; text-align: center; background: #ff8a1f; border-radius: 11px; box-sizing: border-box; }
.forum_guide__title { margin: 0; color: #222; font-size: 16px; font-weight: 700; line-height: 24px; text-align: center; }
.forum_guide__content { margin: 2px 0 0; color: #222; font-size: 15px; font-weight: 600; line-height: 24px; text-align: center; }
.forum_guide__footer { display: flex; align-items: center; justify-content: center; margin-top: 14px; }
.forum_guide__button { width: 128px; height: 38px; padding: 0; color: #fff; font-size: 16px; font-weight: 600; line-height: 38px; background: #ff8a1f; border: 0; border-radius: 19px; }
.forum_guide__button:disabled { opacity: .65; }
</style>
